<template>
  <div class="min-h-screen bg-bochas-bg">
    <section v-if="loading" class="py-24 sm:py-32 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <section
      v-else-if="!referente"
      class="max-w-7xl mx-auto px-4 py-12 lg:py-24 text-center"
    >
      <h1 class="text-2xl sm:text-3xl font-bold text-white mb-3">
        No encontrado
      </h1>
      <p class="text-gray-400 mb-6">No encontramos este jugador.</p>
      <router-link
        to="/jugadores-históricos"
        class="btn-bochas-rojo text-sm sm:text-base"
      >
        Volver a jugadores históricos
      </router-link>
    </section>

    <article v-else class="pb-12 sm:pb-16">
      <section class="py-12 lg:py-24 px-4 sm:px-6 lg:px-8 bg-degradado-bochas">
        <div class="max-w-7xl mx-auto">
          <router-link
            to="/jugadores-históricos"
            class="text-xs sm:text-sm text-white/80 hover:text-white mb-6 inline-block"
          >
            ← Volver a jugadores históricos
          </router-link>
          <div class="flex flex-col sm:flex-row items-center gap-6">
            <div
              class="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden bg-bochas-bg flex items-center justify-center flex-shrink-0 border-4 border-white/10"
            >
              <img
                v-if="referente.photo_url"
                :src="referente.photo_url"
                :alt="referente.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-bochas-bg flex items-center justify-center"
              >
                <span class="text-white text-5xl lg:text-6xl font-bold">{{
                  initials
                }}</span>
              </div>
            </div>
            <div>
              <h1
                class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2 text-center sm:text-left"
              >
                {{ referente.name }}
              </h1>
              <p
                v-if="referente.careerStartDate || referente.careerEndDate"
                class="text-base sm:text-lg text-white/90 text-center sm:text-left"
              >
                {{ careerPeriod(referente) }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="referente.phone || referente.email || referente.whatsapp || referente.facebookUrl || referente.instagramUrl"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8"
      >
        <PlayerContactLinks :player="referente" />
      </div>

      <section
        v-if="referente.achievements?.length"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div class="card-fbochas p-6 sm:p-10">
          <div class="mb-4 flex items-center gap-3">
            <IconFbochas name="trophy" :size="26" color="#d32929" />
            <h2 class="text-xl sm:text-2xl font-bold text-white">Logros</h2>
          </div>
          <ul class="space-y-3">
            <li
              v-for="(achievement, index) in referente.achievements"
              :key="index"
              class="flex items-start gap-3 text-white"
            >
              <span class="mt-1 text-bochas-rojo">•</span>
              <span>{{ achievement }}</span>
            </li>
          </ul>
        </div>
      </section>

      <section
        v-if="referente.bio"
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div class="card-fbochas p-6 sm:p-10">
          <h2 class="text-xl sm:text-2xl font-bold text-white mb-4">
            Su legado
          </h2>
          <RichTextContent
            :html="referente.bio"
            class="text-bochas-text-muted leading-relaxed text-base sm:text-lg"
          />
        </div>
      </section>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { obtenerReferente } from "@/services/referentes/referentesService";
import { careerPeriod } from "@/utils/playerCareer";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import PlayerContactLinks from "@/components/Common/PlayerContactLinks.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";

const route = useRoute();
const referente = ref(null);
const loading = ref(true);

const initials = computed(() => {
  if (!referente.value?.name) return "";
  return referente.value.name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
});

async function load() {
  loading.value = true;
  referente.value = null;
  try {
    referente.value = await obtenerReferente(route.params.id);
    await applyContentSeo({
      title: referente.value.name,
      description: referente.value.bio,
      image: referente.value.photo_url,
    });
  } catch {
    referente.value = null;
    applyNotFoundSeo();
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(() => route.params.id, load);
</script>
